const ENTITIES_INVALIDATED = "ENTITIES_INVALIDATED";

export const ADVICE_INDIVIDUAL_ENTITY = "advice_individual";

export const INDIVIDUAL_CLIENT_ENTITY = "individual_client";

export const ACCOUNT_OPENING_INDIVIDUAL_ENTITY = "account_opening_individual";

export const ADVICE_CORPORATION_ENTITY = "advice_corporation";

export const CORPORATION_CLIENT_ENTITY = "corporation_client";

export const ACCOUNT_OPENING_CORPORATION_ENTITY = "account_opening_corporation";

export const ORDER_INSTRUCTION_ENTITY = "order_instruction";

export const ENDOWMENT_INSURANCE_APPLICATION_ENTITY = "endowment_insurance_application";

export const ENDOWMENT_INSURANCE_APPLICATION_CORPORATION_ENTITY =
  "endowment_insurance_application_corporation";

export interface InvalidatedEntity {
  id: string;
  type: string;
}

export type EntityInvalidationListener = (entities: InvalidatedEntity[]) => void;

export interface EntityInvalidationFilter {
  types?: readonly string[];
}

export interface EntityInvalidationFrame {
  event: string | null;
  data: string;
}

function matchesEntityType(entity: InvalidatedEntity, type: string): boolean {
  return entity.type.toLowerCase() === type.toLowerCase();
}

function invalidatedOfTypes(
  entities: readonly InvalidatedEntity[],
  ...types: readonly string[]
): InvalidatedEntity[] {
  if (types.length === 0) return [...entities];
  return entities.filter((entity) => types.some((type) => matchesEntityType(entity, type)));
}

function payloads(data: Record<string, unknown>): Record<string, unknown>[] {
  const nested =
    data.value !== null && typeof data.value === "object" && !Array.isArray(data.value)
      ? (data.value as Record<string, unknown>)
      : null;
  return nested ? [nested, data] : [data];
}

function parsed(frame: EntityInvalidationFrame): Record<string, unknown> | null {
  if (frame.data === "") return null;
  try {
    const body = JSON.parse(frame.data) as unknown;
    return body !== null && typeof body === "object" && !Array.isArray(body)
      ? (body as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

function namesInvalidation(frame: EntityInvalidationFrame, data: Record<string, unknown>): boolean {
  const wanted = ENTITIES_INVALIDATED.toLowerCase();
  if ((frame.event ?? "").toLowerCase() === wanted) return true;
  const name = typeof data.name === "string" ? data.name : "";
  return name.toLowerCase() === wanted;
}

export function entitiesInvalidatedBy(frame: EntityInvalidationFrame): InvalidatedEntity[] | null {
  const data = parsed(frame);
  if (!data || !namesInvalidation(frame, data)) return null;
  const out: InvalidatedEntity[] = [];
  const seen = new Set<string>();
  for (const source of payloads(data)) {
    const list = source.entities;
    if (!Array.isArray(list)) continue;
    for (const raw of list) {
      if (raw === null || typeof raw !== "object" || Array.isArray(raw)) continue;
      const entry = raw as Record<string, unknown>;
      const type = typeof entry.type === "string" ? entry.type : "";
      if (type === "") continue;
      const id = typeof entry.id === "string" ? entry.id : "";
      const key = `${type.toLowerCase()} ${id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ id, type });
    }
    break;
  }
  return out;
}

export function mergeInvalidated(
  held: readonly InvalidatedEntity[],
  arrived: readonly InvalidatedEntity[],
): InvalidatedEntity[] {
  const key = (e: InvalidatedEntity): string => `${e.type.toLowerCase()} ${e.id}`;
  const out = [...held];
  const seen = new Set(out.map(key));
  for (const e of arrived) {
    if (seen.has(key(e))) continue;
    seen.add(key(e));
    out.push(e);
  }
  return out;
}

interface Registration {
  listener: EntityInvalidationListener;
  types: readonly string[];
}

const registrations = new Set<Registration>();

export function onEntitiesInvalidated(
  listener: EntityInvalidationListener,
  filter: EntityInvalidationFilter = {},
): () => void {
  const registration: Registration = { listener, types: filter.types ?? [] };
  registrations.add(registration);
  return () => {
    registrations.delete(registration);
  };
}

const announced = new WeakSet<EntityInvalidationFrame>();

export function publishEntityInvalidation(
  frame: EntityInvalidationFrame,
  entities: readonly InvalidatedEntity[],
): void {
  if (entities.length === 0 || announced.has(frame)) return;
  announced.add(frame);
  for (const registration of [...registrations]) {
    const batch = invalidatedOfTypes(entities, ...registration.types);
    if (batch.length === 0) continue;
    try {
      registration.listener(batch);
    } catch {}
  }
}
